/**
 * Names for the contract's shapes, so screens do not repeat the lookup.
 *
 * Every type here is read out of the generated schema. Nothing is written by
 * hand, so a change to api/healtrics-openapi.yaml reaches these names the next
 * time the client is generated, and a screen that no longer matches fails to
 * compile.
 */

import type { components } from "@contract";

export type ClinicalRecord = components["schemas"]["ClinicalRecord"];
export type PatientSummary = components["schemas"]["PatientSummary"];
export type Chart = components["schemas"]["Chart"];
export type ParseSuggestion = components["schemas"]["ParseSuggestion"];
export type RecordType = components["schemas"]["RecordType"];
export type AccessRoute = components["schemas"]["AccessRoute"];

/** How each record type reads in a heading. */
export const RECORD_TYPE_LABELS: Record<string, string> = {
  observation: "Measurements",
  condition: "Conditions",
  medication: "Medications",
  document: "Documents",
};

/**
 * What the reader was unsure about, as a doctor reads it.
 *
 * Short, because it sits on a chip beside the measurement. The server sends
 * the condition it found; the wording is this screen's business, and it says
 * what is wrong with the line rather than how a confidence score landed.
 */
export const REVIEW_REASON_LABELS: Record<string, string> = {
  doubled: "Printed twice",
  unclear: "Two numbers on the line",
  no_unit: "No unit found",
  unit_unknown: "Unit not expected",
};

/** The same, as the sentence under the number. */
export const REVIEW_REASON_NOTES: Record<string, string> = {
  doubled:
    "The page printed this number on top of itself. Check it against the " +
    "line below.",
  unclear:
    "More than one number on this line, and no way to tell which is the " +
    "reading.",
  no_unit: "The line carried no unit. Type it before you accept this.",
  unit_unknown:
    "Not a unit this test is normally reported in. A wrong unit can put the " +
    "number out by a factor of ten.",
};

/** How each access route reads in the banner that explains it. */
export const ACCESS_ROUTE_LABELS: Record<string, string> = {
  care_relationship: "You are treating this patient.",
  break_glass: "Emergency access. This is under review.",
  org_admin_oversight: "Organisation oversight. Restricted records stay hidden.",
};

/** The one line each record type shows in a table. */
export function recordSummary(record: ClinicalRecord): string {
  const payload = (record.payload ?? {}) as Record<string, unknown>;

  switch (record.type) {
    case "observation": {
      const value = payload["value_quantity"] ?? payload["value_text"] ?? "";
      const unit = payload["unit"] ?? "";
      return `${payload["display"] ?? ""} ${value} ${unit}`.trim();
    }
    case "condition":
      return String(payload["display"] ?? "");
    case "medication":
      return [payload["medication_text"], payload["dosage_text"]]
        .filter(Boolean)
        .join(" — ");
    case "document":
      return String(payload["title"] ?? "");
    default:
      return "";
  }
}

/** A date and time as a clinic reads it. Malaysia time, because that is where the clinic is. */
export function readableTime(value: string | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-MY", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kuala_Lumpur",
  });
}
